// DefectsDetail.tsx
// 不良情報詳細
import React, {
  useRef,
  useEffect,
  useState,
  RefObject,
  createRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import Modal from "../ui/Modal";
import ModalConfirm from "../ui/ModalConfirm";
import DefectsInput from "../defectsInput/DefectsInput";
import DefectsPreview from "./DefectsPreview";
import {
  DefectsDetailProp,
  removeDefectsDetails,
  updateDefectDetail,
  deleteDefectReports,
  setDefectionTimingId,
} from "../../redux/defectsSlice";
import { ProcCodeSelect } from "../ui/ProcCodeSelect";
import { MachCodeSelect } from "../ui/MachCodeSelect";
import { DefectTypeSelect } from "../ui/DefectTypeSelect";
import cloneDeep from "lodash/cloneDeep";
import "./DefectsDetail.css";
import { toNumber } from "lodash";

interface Props {
  index: number;
  defectsDetail: DefectsDetailProp;
  minFlag: boolean;
}

const DefectsDetail: React.FC<Props> = ({ index, defectsDetail, minFlag }) => {
  const dispatch: AppDispatch = useDispatch();
  // 不良情報
  const defects = useSelector((state: RootState) => state.defects);

  // 不良情報登録モーダル制御
  const [isDefectInputModalOpen, setIsDefectInputModalOpen] = useState(false);
  const openDefectInputModalOpen = () => {
    setIsDefectInputModalOpen(true);
  };
  const closeDefectInputModalOpen = () => setIsDefectInputModalOpen(false);
  const closeModal = () => {
    setIsDefectInputModalOpen(false);
  };
  // 画像プレビューモーダル制御
  const [isDefectsPreviewModalOpen, setIsDefectsPreviewModalOpen] =
    useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const openDefectsPreviewModalOpen = (imageIndex: number) => {
    setPreviewIndex(imageIndex);
    setIsDefectsPreviewModalOpen(true);
  };
  const closeDefectsPreviewModal = () => setIsDefectsPreviewModalOpen(false);

  // 不良詳細の削除処理
  const handleRemove = () => {
    dispatch(removeDefectsDetails(index));
  };

  // プレビュー画像
  const [previewImages, setPreviewImages] = useState<JSX.Element[]>([]);
  const canvasRefs = useRef<RefObject<HTMLCanvasElement>[]>([]);

  // プレビュー画像表示用Canvasを作成
  useEffect(() => {
    canvasRefs.current = [];
    const newImages = defectsDetail.defectsImages.map((element, index) => {
      canvasRefs.current[index] = createRef<HTMLCanvasElement>();
      return (
        <div className="defects_preview">
          <img
            src="images/delete-button.svg"
            alt=""
            className={"defects-icon imade-delete terash "}
            onClick={() => handleImageRemove(index)}
          />
          <canvas
            width={640}
            height={360}
            className="preview_canvas"
            ref={canvasRefs.current[index]}
            onClick={() => openDefectsPreviewModalOpen(index)}
          ></canvas>
        </div>
      );
    });
    setPreviewImages(newImages);
  }, [defectsDetail.defectsImages]);

  // Canvasに画像をセット
  useEffect(() => {
    canvasRefs.current.forEach((element, index) => {
      if (element.current != null) {
        let ctx = element.current.getContext("2d");
        let img = new Image();
        img.src = defectsDetail.defectsImages[index].images.toString();
        img.onload = function () {
          if (ctx != null) {
            const afterHeight: number = Math.floor(
              img.height * (640 / img.width)
            );
            if (afterHeight > 360) {
              const afterWidth: number = Math.floor(640 * (360 / afterHeight));
              const startX = (640 - afterWidth) / 2;
              ctx.drawImage(img, startX, 0, afterWidth, 360);
            } else {
              ctx.drawImage(img, 0, 0, 640, afterHeight);
            }
          }
        };
      }
    });
  }, [previewImages, defectsDetail.defectsImages]);

  // 加工の前後ボタンの切り替え処理
  const handleBeforeClick = (failure_count: number | null) => {
    dispatch(
      setDefectionTimingId({
        detection_timing_id: 1,
        failure_count: failure_count,
      })
    );
  };
  // 加工の前後ボタンの切り替え処理
  const handleAfterClick = (failure_count: number | null) => {
    dispatch(
      setDefectionTimingId({
        detection_timing_id: 2,
        failure_count: failure_count,
      })
    );
  };

  // 作業者選択処理
  const handleMemberChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDefectsDetail = cloneDeep(defectsDetail);
    newDefectsDetail.worker_id = e.target.value;
    dispatch(
      updateDefectDetail({ index: index, newDefectsDetail: newDefectsDetail })
    );
  };

  // 発生加工の更新
  const handleProcCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDefectsDetail = cloneDeep(defectsDetail);
    newDefectsDetail.procCode = e.target.value;
    dispatch(
      updateDefectDetail({ index: index, newDefectsDetail: newDefectsDetail })
    );
  };

  // 機械の更新
  const handleMachCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDefectsDetail = cloneDeep(defectsDetail);
    newDefectsDetail.machCode = e.target.value;
    dispatch(
      updateDefectDetail({ index: index, newDefectsDetail: newDefectsDetail })
    );
  };

  // 不良品数
  const handleDefectiveNumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDefectsDetail = cloneDeep(defectsDetail);
    newDefectsDetail.scrap_qty = Number(e.target.value);
    dispatch(
      updateDefectDetail({ index: index, newDefectsDetail: newDefectsDetail })
    );
  };

  // 不良理由
  const handleDefectsTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDefectsDetail = cloneDeep(defectsDetail);
    if (e.target.value !== "") {
      newDefectsDetail.defect_type_id = toNumber(e.target.value);
    } else {
      newDefectsDetail.defect_type_id = 0;
    }
    
    dispatch(
      updateDefectDetail({ index: index, newDefectsDetail: newDefectsDetail })
    );
  };

  // 発生原因
  const handleCauseChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newDefectsDetail = cloneDeep(defectsDetail);
    newDefectsDetail.root_cause = e.target.value;
    dispatch(
      updateDefectDetail({ index: index, newDefectsDetail: newDefectsDetail })
    );
  };

  // 対策
  const handleCorrectiveActionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const newDefectsDetail = cloneDeep(defectsDetail);
    newDefectsDetail.corrective_action = e.target.value;
    dispatch(
      updateDefectDetail({ index: index, newDefectsDetail: newDefectsDetail })
    );
  };

  // エラーメッセージ
  const errorMessage = defectsDetail.inputErrors.map((d) => {
    return <p className="defects-detail-errors">{d}</p>;
  });

  const [deleteImageIndex, setDeleteImageIndex] = useState(0);
  const handleImageRemove = (imageIndex: number) => {
    setDeleteImageIndex(imageIndex);
    setIsImageDeleteModalOpen(true);
  };

  // 画像削除確認モーダル制御
  const [isImageDeleteModalOpen, setIsImageDeleteModalOpen] = useState(false);
  // 画像削除確認モーダル制御
  const closeImageDeleteModal = () => {
    setIsImageDeleteModalOpen(false);
  };
  const applyImageDelete = () => {
    console.log("イメージ削除API実行");
    if (defectsDetail !== null) {
      const key = defectsDetail.defectsImages[deleteImageIndex].key;
      const newDefectsDetail = cloneDeep(defectsDetail);
      newDefectsDetail.defectsImages.splice(deleteImageIndex, 1);
      dispatch(
        updateDefectDetail({ index: index, newDefectsDetail: newDefectsDetail })
      );
      if (key !== "") {
        dispatch(deleteDefectReports(key));
      }
    }

    setIsImageDeleteModalOpen(false);
  };

  return (
    <div className="defects-detail-container">
      <div className="defects-detail-info-item">
        {
          /* 削除ボタン */
          !defectsDetail.isRegistered ? (
            <img
              src="images/delete.svg"
              alt=""
              className="defects-icon terash defects-detail-terash-item"
              onClick={() => handleRemove()}
            />
          ) : (
            <></>
          )
        }

        {minFlag ? (
          <>
            <div className="defects-detail-failure-count-item">
              <label htmlFor="defects-failure-count">不良発生回数</label>
              <input
                type="text"
                id="defects-failure-count"
                className="defects-detail-input input-140"
                value={
                  defectsDetail.failure_count === null
                    ? ""
                    : defectsDetail.failure_count
                }
                readOnly
                onChange={handleDefectiveNumChange}
              ></input>
            </div>

            <div className="defects-detail-position">
              <label htmlFor="proc_befor">加工の前後</label>
              {defectsDetail.detection_timing_id === 1 ? (
                <>
                  <button id="proc_befor" className="defects-discovery-btn">
                    前/Before
                  </button>
                  <button
                    id="proc_after"
                    className="defects-discovery-btn-disable"
                    onClick={() =>
                      handleAfterClick(defectsDetail.failure_count)
                    }
                  >
                    後/After
                  </button>
                </>
              ) : (
                <>
                  <button
                    id="proc_after"
                    className="defects-discovery-btn-disable"
                    onClick={() =>
                      handleBeforeClick(defectsDetail.failure_count)
                    }
                  >
                    前/Before
                  </button>
                  <button id="proc_after" className="defects-discovery-btn">
                    後/After
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          <></>
        )}

        <div className="defects-detail-id-item">
          <label htmlFor="defects-detail-id"> ID </label>
          <input
            type="text"
            id="defects-detail-id"
            className="defects-detail-input input-140"
            value={
              defectsDetail.defect_type_seq === null
                ? ""
                : defectsDetail.defect_type_seq
            }
            readOnly
            onChange={handleDefectiveNumChange}
          ></input>
        </div>

        <div className="defects-menber-item">
          <label htmlFor="worker">作業者</label>
          <select
            id="worker"
            onChange={handleMemberChange}
            value={defectsDetail.worker_id}
            className="input-110"
          >
            <option value="">-</option>
            {defects.workStatus.map(({ member_id, member_name }) => (
              <option
                key={member_id}
                value={member_id}
              >{`${member_id} : ${member_name}`}</option>
            ))}
          </select>
        </div>

        <div className="defects-detail-proc-item">
          {defectsDetail.isRegistered}
          <ProcCodeSelect
            label="発生加工"
            plantCode={defectsDetail.plantCode}
            selectProcCode={defectsDetail.procCode}
            handleProcCodeChange={handleProcCodeChange}
            filter={defects.procCodeMaster}
          ></ProcCodeSelect>
        </div>

        <div className="defects-detail-mach-item">
          <MachCodeSelect
            label="発生機械"
            plantCode={defectsDetail.plantCode}
            procCode={defectsDetail.procCode}
            selectMachCode={defectsDetail.machCode}
            handleMachCodeChange={handleMachCodeChange}
            filter={defects.machCodeMaster}
          ></MachCodeSelect>
        </div>

        <div className="defects-detail-quantity-item">
          <label htmlFor="quantity">不良品数</label>
          <input
            type="number"
            inputMode="numeric"
            id="quantity"
            className="defects-detail-input input-140"
            value={defectsDetail.scrap_qty}
            readOnly={defectsDetail.isRegistered ? true : false}
            onChange={handleDefectiveNumChange}
          ></input>
        </div>

        <div className="defects-detail-reason-item">
          <DefectTypeSelect
            label="不良品理由"
            selectDefectsType={defectsDetail.defect_type_id.toString()}
            handleDefectsTypeChange={handleDefectsTypeChange}
          ></DefectTypeSelect>
        </div>
      </div>

      <div className="defects-detail-cause-item">
        <div className="defects-textbox-container">
          <label htmlFor="cause">発生原因</label>
          <textarea
            id="cause"
            className="cause defects-textarea"
            value={defectsDetail.root_cause}
            onChange={handleCauseChange}
          ></textarea>
        </div>
      </div>

      <div className="defects-detail-measures-item">
        <div className="defects-textbox-container">
          <label htmlFor="measures">対策</label>
          <textarea
            id="measures"
            className="measures defects-textarea"
            value={defectsDetail.corrective_action}
            onChange={handleCorrectiveActionChange}
          ></textarea>
        </div>
      </div>
      <div className="defects-detail-errors-item">{errorMessage}</div>
      {/* 不良モード詳細 */}
      <div className="defects-detail-images-item defects-mode-detail-container">
        <div className="defects-mode-detail-label">
          <span>不良モード詳細</span>
        </div>
        <div className="defects-mode-detail-image-container">
          {previewImages}
          <img
            src="images/defect-report.svg"
            alt="追加ボタン"
            className="defects_report_icon"
            onClick={openDefectInputModalOpen}
          />
          <img
            src="images/defects-add.svg"
            alt="追加ボタン"
            className="defects-icon mode-add-btn"
            onClick={openDefectInputModalOpen}
          />
        </div>
      </div>
      <div className="defects-detail-sline-item sepalate-sub-line" />

      {/* 不良情報登録モーダル */}
      <Modal
        isOpen={isDefectInputModalOpen}
        onClose={closeDefectInputModalOpen}
        width="90vw"
        height="90vh"
        contentClassName={""}
      >
        <DefectsInput index={index} closeModal={closeModal} />
      </Modal>
      {/* 画像プレビューモーダル */}
      <Modal
        isOpen={isDefectsPreviewModalOpen}
        onClose={closeDefectsPreviewModal}
        width="90vw"
        height="90vh"
        contentClassName={""}
      >
        <DefectsPreview
          index={previewIndex}
          images={defectsDetail.defectsImages}
        />
      </Modal>
      {/* 画像削除確認モーダル */}
      <Modal
        isOpen={isImageDeleteModalOpen}
        onClose={closeImageDeleteModal}
        width="640px"
        height="240px"
        contentClassName={""}
      >
        <ModalConfirm
          onApply={applyImageDelete}
          onCancel={closeImageDeleteModal}
          title={"画像削除の確認"}
          message={
            "画像を削除すると元に戻せません。削除してよろしいでしょうか?"
          }
          applyButtonMessage={"Apply"}
          cancelButtonMessage={"Cancel"}
        ></ModalConfirm>
      </Modal>
    </div>
  );
};

export default DefectsDetail;
